// Shared "timeline scroll" behavior — a now-anchored chronological list where the
// first current/upcoming row is parked at the top of a fixed-height scroll box
// (past rows scroll up, future scroll down), with a sticky header and a floating
// "Jump to today" button. Extracted from EventsList.jsx so the distribution tabs
// (Bandsintown / TicketWeb / Resident Advisor) share identical behavior.
//
// Markup contract for the consuming view:
//   <div ref={listWrapRef} className="relative">
//     <div data-timeline-scroll onScroll={onListScroll}
//          style={{ height: listH ? `${listH}px` : '70vh' }}
//          className="border border-border rounded-md overflow-auto relative">
//       <table> <thead className="... sticky top-0 z-10"> … </thead>
//         <tbody>{rows.map((r, i) => (
//           <tr data-first-upcoming={i === firstUpcomingIdx || undefined}>…</tr>
//         ))}</tbody>
//       </table>
//     </div>
//     <JumpToTodayButton show={showJump} onClick={() => goHome(true)} />
//   </div>

import { useLayoutEffect, useRef, useState } from 'react';
import { LocateFixed } from 'lucide-react';

export function useTimelineScroll({ ready = true, chronological = true, deps = [] } = {}) {
  const listWrapRef = useRef(null);
  const [listH, setListH] = useState(null);
  const [showJump, setShowJump] = useState(false);
  const didAnchor = useRef(false);

  const findScroll = () =>
    Array.from(document.querySelectorAll('[data-timeline-scroll]')).find((n) => n.offsetParent !== null);

  // Distance to scroll so the first-upcoming row sits flush under the sticky head.
  const homeDelta = (c) => {
    const r = c.querySelector('[data-first-upcoming]');
    if (!r) return c.scrollHeight - c.clientHeight - c.scrollTop; // no upcoming → bottom
    const head = c.querySelector('thead, [data-sticky-head]');
    return r.getBoundingClientRect().top - c.getBoundingClientRect().top - (head ? head.offsetHeight : 0);
  };

  const goHome = (smooth) => {
    const c = findScroll();
    if (!c) return;
    c.scrollBy({ top: homeDelta(c), behavior: smooth ? 'smooth' : 'auto' });
    setShowJump(false);
  };

  const onListScroll = (e) => {
    if (!chronological) { setShowJump(false); return; }
    setShowJump(Math.abs(homeDelta(e.currentTarget)) > 64);
  };

  // Park on the first-upcoming row once data is ready; re-arm when chronological flips.
  useLayoutEffect(() => { if (chronological) didAnchor.current = false; }, [chronological]);
  useLayoutEffect(() => {
    if (!ready || !chronological || didAnchor.current) return;
    if (!findScroll()) return;
    goHome(false);
    didAnchor.current = true;
    const t = setTimeout(() => goHome(false), 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, chronological, ...deps]);

  // Size the scroll box to fill the viewport down to the bottom.
  useLayoutEffect(() => {
    const calc = () => {
      const el = listWrapRef.current;
      if (!el) return;
      setListH(Math.max(260, Math.round(window.innerHeight - el.getBoundingClientRect().top - 16)));
    };
    calc();
    window.addEventListener('resize', calc);
    return () => window.removeEventListener('resize', calc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, ...deps]);

  return { listWrapRef, listH, showJump, goHome, onListScroll };
}

export function JumpToTodayButton({ show, onClick }) {
  if (!show) return null;
  return (
    <button
      onClick={onClick}
      title="Jump back to today"
      className="absolute bottom-4 right-4 z-20 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary text-primary-foreground text-xs font-medium shadow-lg hover:opacity-90"
    >
      <LocateFixed size={14} /> Today
    </button>
  );
}
